/*!
 * Copyright © 2005-2025 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

import { CommonModule } from '@angular/common';
import { Input, Component, inject, OnInit, signal, ViewEncapsulation, HostListener } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { catchError, of, switchMap } from 'rxjs';
import { PageHeaderComponent, PageStateComponent, ThaiDatePipe } from '@alfresco/aca-shared';
import { FlowState, PersonRef, TemplateInstance, WorkflowService, WorkflowTemplate } from './workflow.service';
import { WorkflowDialogComponent } from './workflow-dialog.component';
import { MyTasksService } from './my-tasks.service';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { FlowCanvasComponent, FlowSelection, NODE_ICONS } from './flow-canvas.component';
import {
  FlowConditionOp,
  FlowEdge,
  FlowGraph,
  FlowNode,
  FlowNodeKind,
  FlowProblem,
  NODE_HEIGHT,
  NODE_WIDTH,
  flowSteps,
  fromSteps,
  validateGraph
} from './flow-graph';
import { FlowDeployService } from './flow-deploy.service';

@Component({
  selector: 'app-workflow-builder',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatTooltipModule,
    PageHeaderComponent,
    PageStateComponent,
    ThaiDatePipe,
    TranslatePipe,
    FlowCanvasComponent
  ],
  encapsulation: ViewEncapsulation.None,
  styleUrl: './workflow-builder.component.scss',
  templateUrl: './workflow-builder.component.html'
})
export class WorkflowBuilderComponent implements OnInit {
  /**
   * Whether the screen is shown inside the settings page rather than on a
   * route of its own — it then drops its own title, because the page it
   * sits in already names it.
   */
  @Input() embedded = false;

  private readonly translate = inject(TranslateService);
  private readonly wf = inject(WorkflowService);
  private readonly deployer = inject(FlowDeployService);
  private readonly dialog = inject(MatDialog);
  private readonly snack = inject(MatSnackBar);
  private readonly tasks = inject(MyTasksService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);

  readonly templates = signal<WorkflowTemplate[]>([]);
  readonly instances = signal<TemplateInstance[]>([]);
  readonly busy = signal(false);
  readonly activeProcesses = signal(0);
  readonly engineDefinitions = signal(0);

  editing: WorkflowTemplate | null = null;
  /** The editing template's graph — always there while editing. */
  graph: FlowGraph = { nodes: [], edges: [] };
  selection: FlowSelection = null;
  problems: FlowProblem[] = [];
  /** A step list was just redrawn as a graph; it says so until saved. */
  converted = false;
  dirty = false;
  private counter = 0;

  /** What the palette adds, in the order it lists them. */
  readonly paletteKinds: { kind: FlowNodeKind; label: string; hint: string; tone: string }[] = [
    { kind: 'approval', label: 'DMS.FLOW.KIND_APPROVAL', hint: 'DMS.FLOW.HINT_APPROVAL', tone: 'green' },
    { kind: 'task', label: 'DMS.FLOW.KIND_TASK', hint: 'DMS.FLOW.HINT_TASK', tone: 'blue' },
    { kind: 'condition', label: 'DMS.FLOW.KIND_CONDITION', hint: 'DMS.FLOW.HINT_CONDITION', tone: 'amber' },
    { kind: 'parallel', label: 'DMS.FLOW.KIND_PARALLEL', hint: 'DMS.FLOW.HINT_PARALLEL', tone: 'amber' },
    { kind: 'end', label: 'DMS.FLOW.KIND_END', hint: 'DMS.FLOW.HINT_END', tone: 'red' }
  ];
  readonly icons = NODE_ICONS;

  /** The operators a condition offers, in the order the dropdown shows them. */
  readonly condOps: FlowConditionOp[] = ['eq', 'ne', 'gt', 'gte', 'lt', 'lte', 'contains', 'empty', 'notEmpty'];

  private static readonly OP_SYMBOLS: Record<FlowConditionOp, string> = {
    eq: '=',
    ne: '≠',
    gt: '>',
    gte: '≥',
    lt: '<',
    lte: '≤',
    contains: '∋',
    empty: 'empty',
    notEmpty: '!empty'
  };

  ngOnInit(): void {
    this.wf.ensureTemplatesLoaded().subscribe(() => {
      this.load();
      // manage page's Edit action lands here with ?edit=<templateId>
      const editId = this.route.snapshot.queryParamMap.get('edit');
      if (editId) {
        const tpl = this.wf.getTemplates().find((t) => t.id === editId);
        if (tpl) {
          this.editTemplate(tpl);
        }
      }
    });
    this.tasks.counts().subscribe((counts) => this.activeProcesses.set(counts.running));
    this.wf.listProcessDefinitions(true).subscribe({
      next: (defs) => this.engineDefinitions.set(defs.length),
      error: () => this.engineDefinitions.set(0)
    });
  }

  load(): void {
    this.templates.set(this.wf.getTemplates());
    this.wf.listInstances().subscribe((all) => this.instances.set(all.filter((i) => i.status === 'running' || i.status === 'returned')));
  }

  state(tpl: WorkflowTemplate): FlowState {
    return WorkflowService.flowState(tpl);
  }

  stepCount(tpl: WorkflowTemplate): number {
    return tpl.graph ? flowSteps(tpl.graph).length : tpl.steps.length;
  }

  opLabel(op: FlowConditionOp | undefined): string {
    return WorkflowBuilderComponent.OP_SYMBOLS[op ?? 'eq'];
  }

  /** Edge labels and node subtitles for the canvas, in the reader's language. */
  canvasLabels() {
    const t = (key: string) => this.translate.instant(key);
    return {
      approve: t('DMS.FLOW.OUT_APPROVE'),
      reject: t('DMS.FLOW.OUT_REJECT'),
      otherwise: t('DMS.FLOW.OTHERWISE'),
      anyone: t('DMS.FLOW.WHO_ANYONE'),
      initiator: t('DMS.FLOW.WHO_INITIATOR'),
      unassigned: t('DMS.FLOW.WHO_NOBODY')
    };
  }

  // ---- editing ---------------------------------------------------------------------

  newTemplate(): void {
    const t = (key: string) => this.translate.instant(key);
    // a start, one approval and an end, already joined — the smallest flow that runs
    const start: FlowNode = { id: 'start', kind: 'start', name: t('DMS.FLOW.KIND_START'), x: 120, y: 40 };
    const approval: FlowNode = { id: this.uid('n'), kind: 'approval', name: t('DMS.FLOW.KIND_APPROVAL'), x: 120, y: 170, assigneeType: 'person' };
    const end: FlowNode = { id: 'end', kind: 'end', name: t('DMS.FLOW.KIND_END'), x: 120, y: 300 };
    this.graph = {
      nodes: [start, approval, end],
      edges: [
        { id: this.uid('e'), from: start.id, to: approval.id },
        { id: this.uid('e'), from: approval.id, to: end.id }
      ]
    };
    this.editing = { id: this.uid('tpl'), name: t('DMS.UI.89CCF2'), description: '', steps: [], graph: this.graph };
    this.converted = false;
    this.dirty = true;
    this.selection = { type: 'node', id: approval.id };
    this.check();
  }

  /** Opens a template; one still held as a step list is redrawn as the graph it meant. */
  editTemplate(tpl: WorkflowTemplate): void {
    const copy: WorkflowTemplate = JSON.parse(JSON.stringify(tpl));
    this.converted = !copy.graph;
    this.graph = copy.graph ?? fromSteps(copy.steps);
    copy.graph = this.graph;
    this.editing = copy;
    this.selection = null;
    this.dirty = this.converted;
    this.check();
  }

  cancelEdit(): void {
    this.editing = null;
    this.selection = null;
  }

  /** Validates again after any change, so the problem list and the canvas marks stay current. */
  check(): void {
    this.problems = validateGraph(this.graph);
  }

  onCanvasChanged(): void {
    this.dirty = true;
    this.check();
  }

  selectedNode(): FlowNode | null {
    const sel = this.selection;
    return sel?.type === 'node' ? (this.graph.nodes.find((n) => n.id === sel.id) ?? null) : null;
  }

  selectedEdge(): FlowEdge | null {
    const sel = this.selection;
    return sel?.type === 'edge' ? (this.graph.edges.find((e) => e.id === sel.id) ?? null) : null;
  }

  sourceOf(edge: FlowEdge): FlowNode | undefined {
    return this.graph.nodes.find((n) => n.id === edge.from);
  }

  nodeName(id: string): string {
    return this.graph.nodes.find((n) => n.id === id)?.name ?? id;
  }

  /** Adds a box below everything else, and selects it. */
  addNode(kind: FlowNodeKind): void {
    const lowest = this.graph.nodes.reduce((max, n) => Math.max(max, n.y), 0);
    const anchor = this.selectedNode();
    const node: FlowNode = {
      id: this.uid('n'),
      kind,
      name: this.translate.instant(`DMS.FLOW.KIND_${kind.toUpperCase()}`),
      x: anchor ? anchor.x + NODE_WIDTH + 60 : 120,
      y: anchor ? anchor.y : lowest + NODE_HEIGHT + 70,
      ...(kind === 'approval' || kind === 'task' ? { assigneeType: 'person' as const } : {})
    };
    this.graph.nodes.push(node);
    this.selection = { type: 'node', id: node.id };
    this.onCanvasChanged();
  }

  /** The start box can go only if the flow has another one; anything else can go with its edges. */
  deleteSelected(): void {
    const node = this.selectedNode();
    const edge = this.selectedEdge();
    if (node) {
      if (node.kind === 'start' && this.graph.nodes.filter((n) => n.kind === 'start').length === 1) {
        return;
      }
      this.graph.nodes = this.graph.nodes.filter((n) => n.id !== node.id);
      this.graph.edges = this.graph.edges.filter((e) => e.from !== node.id && e.to !== node.id);
    } else if (edge) {
      this.graph.edges = this.graph.edges.filter((e) => e.id !== edge.id);
    } else {
      return;
    }
    this.selection = null;
    this.onCanvasChanged();
  }

  @HostListener('document:keydown', ['$event'])
  onKey(event: KeyboardEvent): void {
    const tag = (event.target as HTMLElement)?.tagName;
    if (!this.editing || tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') {
      return;
    }
    if (event.key === 'Delete' || event.key === 'Backspace') {
      event.preventDefault();
      this.deleteSelected();
    }
  }

  chooseAssignee(node: FlowNode): void {
    const mode = node.assigneeType === 'group' ? 'pickGroup' : 'pickPerson';
    this.dialog
      .open(WorkflowDialogComponent, { data: { mode, title: 'DMS.UI.EA4D7B' } })
      .afterClosed()
      .subscribe((person: PersonRef) => {
        if (person?.id) {
          node.assignee = person.id;
          node.assigneeLabel = person.displayName;
          this.onCanvasChanged();
        }
      });
  }

  setAssigneeType(node: FlowNode, type: 'person' | 'group' | 'initiator'): void {
    if (node.assigneeType !== type) {
      node.assigneeType = type;
      node.assignee = '';
      node.assigneeLabel = '';
      this.onCanvasChanged();
    }
  }

  /** An approval's path: approve, reject, or both. */
  setOutcome(edge: FlowEdge, outcome: 'approve' | 'reject' | 'both'): void {
    edge.outcome = outcome === 'both' ? undefined : outcome;
    this.onCanvasChanged();
  }

  /** A condition's path: taken when its condition holds, or when no other one does. */
  setDefault(edge: FlowEdge, isDefault: boolean): void {
    for (const other of this.graph.edges.filter((e) => e.from === edge.from)) {
      other.isDefault = false;
    }
    edge.isDefault = isDefault;
    if (isDefault) {
      delete edge.condition;
    } else {
      edge.condition ??= { field: '', op: 'eq', value: '' };
    }
    this.onCanvasChanged();
  }

  /** The words for one problem, with the box or line it concerns. */
  problemText(problem: FlowProblem): string {
    const where = problem.nodeId ? this.nodeName(problem.nodeId) : problem.edgeId ? this.translate.instant('DMS.FLOW.A_LINE') : '';
    return this.translate.instant(`DMS.FLOW.P_${problem.code}`, { p0: where });
  }

  selectProblem(problem: FlowProblem): void {
    if (problem.nodeId) {
      this.selection = { type: 'node', id: problem.nodeId };
    } else if (problem.edgeId) {
      this.selection = { type: 'edge', id: problem.edgeId };
    }
  }

  // ---- visibility gate -------------------------------------------------------------

  /** Pick a person or a group the visibility gate should let through. */
  addAllowed(kind: 'user' | 'group'): void {
    const tpl = this.editing;
    if (!tpl) {
      return;
    }
    this.dialog
      .open(WorkflowDialogComponent, {
        data: { mode: kind === 'group' ? 'pickGroup' : 'pickPerson', title: 'DMS.BUILDER.VIS_ADD' }
      })
      .afterClosed()
      .subscribe((p: PersonRef) => {
        if (!p?.id) {
          return;
        }
        const list = kind === 'group' ? (tpl.allowedGroups ??= []) : (tpl.allowedUsers ??= []);
        if (!list.some((x) => x.id === p.id)) {
          list.push(p);
        }
      });
  }

  removeAllowed(kind: 'user' | 'group', index: number): void {
    (kind === 'group' ? this.editing?.allowedGroups : this.editing?.allowedUsers)?.splice(index, 1);
  }

  // ---- save & publish --------------------------------------------------------------

  /** Saves the drawing. A published flow keeps running its published version until the next publish. */
  saveTemplate(): void {
    if (!this.editing) {
      return;
    }
    this.busy.set(true);
    this.wf.saveTemplate(this.editing).subscribe({
      next: () => {
        this.busy.set(false);
        this.snack.open(this.translate.instant('DMS.UI.517098'), this.translate.instant('DMS.UI.55C866'), { duration: 2500 });
        this.editing = null;
        this.load();
      },
      error: () => {
        this.busy.set(false);
        this.snack.open(this.translate.instant('DMS.UI.1D700C'), this.translate.instant('DMS.UI.55C866'), { duration: 4000 });
      }
    });
  }

  /**
   * Saves, then deploys the graph to the process engine as the next version of
   * this flow's process definition. From then on the wizard starts it as a
   * real process; flows already running keep the version they started on.
   */
  publish(): void {
    const tpl = this.editing;
    if (!tpl || this.problems.length) {
      return;
    }
    this.busy.set(true);
    this.wf
      .saveTemplate(tpl)
      .pipe(
        switchMap(() => this.wf.currentUserId()),
        switchMap((by) => {
          const saved = this.wf.getTemplates().find((t) => t.id === tpl.id) ?? tpl;
          return this.deployer.publish(saved, by);
        }),
        switchMap((deployment) => this.wf.setDeployment(tpl.id, deployment)),
        catchError((err) => {
          // eslint-disable-next-line no-console
          console.error('Publishing the flow failed', err);
          this.busy.set(false);
          this.snack.open(this.translate.instant('DMS.FLOW.PUBLISH_FAILED'), this.translate.instant('DMS.UI.55C866'), { duration: 5000 });
          return of(null);
        })
      )
      .subscribe((done) => {
        if (done === null) {
          return;
        }
        this.busy.set(false);
        this.snack.open(this.translate.instant('DMS.FLOW.PUBLISHED'), this.translate.instant('DMS.UI.55C866'), { duration: 3000 });
        this.editing = null;
        this.load();
      });
  }

  deleteTemplate(tpl: WorkflowTemplate): void {
    this.wf.deleteTemplate(tpl.id).subscribe({
      next: () => this.load(),
      error: () => this.snack.open(this.translate.instant('DMS.UI.1D700C'), this.translate.instant('DMS.UI.55C866'), { duration: 4000 })
    });
  }

  // ---- running ------------------------------------------------------------------------

  /** A published flow starts from the wizard, where its documents are chosen; a step list starts here as before. */
  runTemplate(tpl: WorkflowTemplate): void {
    if (WorkflowService.runsAsProcess(tpl)) {
      void this.router.navigate(['/workflows/start']);
      return;
    }
    const first = this.wf.nextRunnableStep(tpl, 0, {});
    const step = first >= 0 ? tpl.steps[first] : undefined;
    if (!step?.assignee) {
      this.snack.open(this.translate.instant('DMS.UI.BE70BE'), this.translate.instant('DMS.UI.55C866'), { duration: 3000 });
      return;
    }
    this.busy.set(true);
    this.wf
      .startStep(step, `${tpl.name} — step ${first + 1}: ${step.name}`)
      .pipe(
        switchMap((proc) => this.wf.processTasks(proc.id).pipe(switchMap((tks) => of({ proc, taskId: tks[0]?.id })))),
        catchError((e) => {
          // eslint-disable-next-line no-console
          console.error('Run template failed', e);
          this.snack.open(this.translate.instant('DMS.UI.1D700C'), this.translate.instant('DMS.UI.55C866'), { duration: 4000 });
          return of(null);
        })
      )
      .subscribe((res: any) => {
        this.busy.set(false);
        if (res) {
          const inst: TemplateInstance = {
            id: this.uid('inst'),
            templateId: tpl.id,
            templateName: tpl.name,
            templateVersion: tpl.version ?? 1,
            currentStep: first,
            status: 'running',
            taskId: res.taskId,
            processId: res.proc.id,
            startedAt: new Date().toISOString(),
            history: [],
            vars: {}
          };
          this.snack.open(
            this.translate.instant('DMS.UI.009417', { p0: tpl.name, p1: step.assigneeLabel || step.assignee }),
            this.translate.instant('DMS.UI.55C866'),
            {
              duration: 4000
            }
          );
          this.wf
            .createInstance(inst)
            .pipe(catchError(() => of(null)))
            .subscribe(() => this.load());
        }
      });
  }

  private uid(prefix: string): string {
    return `${prefix}-${Math.floor(performance.now() * 1000)}-${this.counter++}`;
  }
}
